import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Link } from 'react-router';
import api from '../../../api';
import type { MatchDaySummary } from '../../../api/types';
import MatchDayDetail from './MatchDayDetail';

const PAGE = 25;

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pl-PL', {
  weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
});

export default function MatchDays() {
  const theme = useTheme();
  // A 6-column table does not fit the Fold's cover screen, and the action column ends up
  // behind a horizontal scroll. Below sm each Match Day is a card instead.
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<MatchDaySummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [mutableId, setMutableId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  // No synchronous setState in here: it is called from an effect.
  const load = useCallback((before?: string) => (
    api.matchDays({ limit: PAGE, ...(before ? { before } : {}) })
      .then((response) => {
        if (response.statusCode !== 200) {
          setError(response.messages.join(' ') || 'Nie udało się pobrać dni meczowych');
          return;
        }
        setError(null);
        setRows((current) => (before ? [...current, ...response.matchDays] : response.matchDays));
        setCursor(response.nextCursor);
        setMutableId(response.mutableId);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  ), []);

  useEffect(() => { void load(); }, [load]);

  const loadMore = (before: string) => {
    setLoading(true);
    void load(before);
  };

  const remove = (row: MatchDaySummary) => {
    if (!window.confirm(
      `Usunąć dzień meczowy ${formatDate(row.date)}? ${row.matchCount} meczów i `
      + `${row.participantCount} wyników zniknie bezpowrotnie.`
    )) {
      return;
    }
    setRemoving(row._id);
    api.removeMatchDay(row._id)
      .then((response) => {
        if (response.statusCode !== 200) {
          setError(response.messages.join(' ') || 'Nie udało się usunąć');
          return undefined;
        }
        setOpen(null);
        return load();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setRemoving(null));
  };

  const toggle = (id: string) => setOpen(open === id ? null : id);

  const expander = (id: string) => (
    <IconButton
      size="small"
      aria-label={open === id ? 'Zwiń szczegóły' : 'Pokaż szczegóły'}
      aria-expanded={open === id}
      onClick={() => toggle(id)}
    >{open === id ? '▾' : '▸'}
    </IconButton>
  );

  const actions = (row: MatchDaySummary) => (
    row._id === mutableId
      ? (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button component={Link} to={`/admin/match-days/${row._id}/edit`} size="small">
            Edytuj
          </Button>
          <Button
            size="small"
            color="error"
            disabled={removing !== null}
            onClick={() => remove(row)}
          >Usuń</Button>
        </Stack>
      )
      : null
  );

  return (
    <Stack className="matchDays" spacing={2}>
      <div>
        <Typography variant="h6">Dni meczowe</Typography>
        <Typography variant="body2" color="text.secondary">
          Usunąć można tylko ostatni dzień meczowy: kary za nieobecność liczą się od
          poprzedniego dnia, więc zmiana wcześniejszego zmieniłaby punkty w następnych.
        </Typography>
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      {rows.length === 0 && !loading && !error && (
        <Typography variant="body2" color="text.secondary">
          Nie ma jeszcze żadnego dnia meczowego.
        </Typography>
      )}

      {rows.length > 0 && compact && (
        <Stack spacing={1}>
          {rows.map((row) => (
            <Paper key={row._id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {expander(row._id)}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(row.date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.participantCount} osób · {row.matchCount} meczów
                    {row.penaltyCount > 0 && ` · ${row.penaltyCount} kar`}
                    {` · ${row.points} pkt`}
                  </Typography>
                </Box>
                {row.comment && <Chip size="small" variant="outlined" label="komentarz" />}
                {actions(row)}
              </Stack>
              <Collapse in={open === row._id} unmountOnExit>
                <Divider sx={{ my: 1 }} />
                <MatchDayDetail id={row._id} />
              </Collapse>
            </Paper>
          ))}
        </Stack>
      )}

      {rows.length > 0 && !compact && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 44 }} />
                <TableCell>Data</TableCell>
                <TableCell align="right">Osób</TableCell>
                <TableCell align="right">Meczów</TableCell>
                <TableCell align="right">Kar</TableCell>
                <TableCell align="right">Punktów</TableCell>
                <TableCell sx={{ width: 150 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => ([
                <TableRow key={row._id} hover>
                  <TableCell>{expander(row._id)}</TableCell>
                  <TableCell>
                    {formatDate(row.date)}
                    {row.comment && (
                      <Chip size="small" label="komentarz" sx={{ ml: 1 }} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">{row.participantCount}</TableCell>
                  <TableCell align="right">{row.matchCount}</TableCell>
                  <TableCell align="right">{row.penaltyCount || ''}</TableCell>
                  <TableCell align="right">{row.points}</TableCell>
                  <TableCell align="right">{actions(row)}</TableCell>
                </TableRow>,
                <TableRow key={`${row._id}-detail`}>
                  <TableCell sx={{ py: 0, borderBottom: 0 }} colSpan={7}>
                    <Collapse in={open === row._id} unmountOnExit>
                      <Box sx={{ pl: 2 }}><MatchDayDetail id={row._id} /></Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              ]))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        {loading && <CircularProgress size={18} />}
        {cursor && !loading && (
          <Button variant="outlined" size="small" onClick={() => loadMore(cursor)}>
            Pokaż więcej
          </Button>
        )}
        {rows.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {rows.length} {cursor ? 'z listy' : 'wszystkich'}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
